import { CAPSTONE } from '@rsos/constants/appNames';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import { fetchAlerts } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import { fetchLanguageList } from '@rsos/reduxDataStore/src/features/chats/chatsSlice';
import {
  describeProfileOrganizations,
  describeUserOrganizationsState,
  fetchUser,
  postAuthTokenSuccess,
  selectUserOrgByApp,
  setTokens,
  sessionStart,
} from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import { getRspAlertQueueMaxSize } from '@rsos/utils/metaTags';
import { configureUserScope } from '@rsos/utils/sentry';
import canViewAlerts from '../utils/canViewAlerts';
import {
  APP_INIT_START,
  APP_INIT_SUCCESS,
  APP_INIT_FAILED,
} from './actionTypes';

const RSP_ALERT_QUEUE_MAX_SIZE = getRspAlertQueueMaxSize();

export const appInitStart = () => ({
  type: APP_INIT_START,
});

export const appInitSuccess = () => ({
  type: APP_INIT_SUCCESS,
});

export const appInitFailed = error => ({
  type: APP_INIT_FAILED,
  error,
});

const hardRedirect = to => {
  window.location = to;
};

const initialize = () => (dispatch, store) => {
  dispatch(appInitStart());

  // If using the auto login/query feature, parse the token and set the
  // token to localStorage and the store to be used if user navigates to
  // another app
  const isLoggedIn = store().sinatra.user.isLoggedIn;
  const accessToken = getUrlParameter('access_token');
  const {
    sinatra: {
      auth: {
        session: { sessionStart: existingSessionStart },
      },
      irp: { irp_access_token },
    },
  } = store();

  const token = irp_access_token ? irp_access_token : accessToken;

  if (token) {
    const tokens = {
      refresh_token: null,
      token: token,
    };
    // Business logic:
    // - a fresh URL `access_token` represents a new bootstrap/login event and
    //   should start a new 48-hour portal session clock
    // - a persisted IRP token on plain app refresh should not reset that clock
    //
    // This preserves the existing auto-login flow while keeping reloads
    // backward compatible with persisted session state from redux-persist.
    const shouldStartSession = accessToken || !existingSessionStart;

    setTokens(tokens);
    dispatch(postAuthTokenSuccess(tokens));
    if (shouldStartSession) {
      dispatch(sessionStart());
      gspx.trackEvent({
        name: 'session_start_from_query_params',
        type: GPX_EVENT_TYPES.INFO,
        feature: GPX_FEATURES.LEGACY,
        capability: GPX_CAPABILITIES.LEGACY,
        route: GPX_ROUTES.NAVBAR,
        additionalKeys: {
          legacy_category: 'Auth Session',
          legacy_name: 'Session Start from query params',
          Category: 'Session tracking',
          'Launched date': new Date(),
        },
      });
    }
  }

  // Kept so the failure path can tell an orgless /user response apart from
  // something replacing the profile in the store afterwards. Different fixes.
  let fetchedProfile;

  return dispatch(fetchUser())
    .then(user => {
      fetchedProfile = user?.profile;
      if (user && user.profile) {
        configureUserScope(user.profile);
      }
    })
    .then(() => {
      const { sinatra } = store();
      const org = selectUserOrgByApp(sinatra, CAPSTONE);
      if (!org?.name) {
        throw new Error(
          `No ${CAPSTONE} organization for the current user, so the portal ` +
            `cannot load. store: ${describeUserOrganizationsState(sinatra.user)}` +
            `; /user response: ${describeProfileOrganizations(fetchedProfile)}` +
            `; storeProfileIsFetchedProfile=${sinatra.user?.profile === fetchedProfile}`,
        );
      }
      return dispatch(fetchPsap(`/psaps/${org.name}?invert_polygons=true`));
    })
    .then(() => {
      const initializationPromises = [];
      const { psaps, sinatra } = store();
      const orgName = sinatra.user.currentOrg.name;
      const hasAlertsAccess = canViewAlerts(sinatra, psaps);
      if (hasAlertsAccess) {
        initializationPromises.push(
          dispatch(
            fetchAlerts({
              orgName,
              queueMaxSize: RSP_ALERT_QUEUE_MAX_SIZE,
              endTime: new Date().getTime(),
            }),
          ),
        );
      }
      initializationPromises.push(dispatch(fetchLanguageList(orgName)));
      return Promise.all(initializationPromises);
    })
    .then(() => dispatch(appInitSuccess()))
    .catch(action => {
      // eslint-disable-next-line no-console -- error visibility and Sentry breadcrumbs
      console.error('App initialization failed', action.error || action);

      // action can either be action with an error or a JS error.
      if (isLoggedIn) {
        return dispatch(appInitFailed(action.error || action));
      }

      return hardRedirect('/');
    });
};

export default initialize;
